import { CSmartTable } from '@coreui/react-pro'
import CopyAll from '@mui/icons-material/CopyAll'
import { Chip, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import capitalize from 'lodash/capitalize'
import { useEffect, useState } from 'react'
import { BrandPayoutStyledSmartTable } from 'src/clinic/views/clinicBrandPayoutReports/styledComponents'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  copyToClipBoard,
  formatPhoneNumber,
  getCSmartTableColumnData,
  getExactDiffFromCurrentTimeDayJs,
  getExactDiffFromCurrentTimeDayJsWithExtraData,
  getOrdinalSuffix,
  getTimezoneOffset,
} from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { preparePatientLabelFromPatientInfoObject } from '../utils'
import utc from 'dayjs/plugin/utc'
import ListingVerticalStripForEventStatus, {
  VerticalStripForEventStatusUIOnly,
} from 'src/mentor/components/MentorEventsPage/components/bookingListing/LeftVerticalStripForEventStatus'

dayjs.extend(utc)

export default function ScheduledPatientsTable({
  eventType,
  clinicBrandParam = 'clinicBrandIds',
  clinicLocationParam = 'clinicIds',
  clinicBrandIds,
  eventTypeParam = 'eventTypes',
  clinicIds,
  isEventTypesNotMandatory = false,
  toInDayJsFormat,
  fromInDayJsFormat,
  listingItemTransformer = (data) => data,
  hideClinicDetails = false,
  reportFetchingApiURL,
  hideContactDetails = false,
  patientVisitInfoLabel = 'Visit Information',
}) {
  const [loadingTableData, setLoadingTableData] = useState(false)
  const [scheduledPatientsData, setScheduledPatientsData] = useState()

  async function getScheduledPatients() {
    setLoadingTableData(true)
    let params = {
      [eventTypeParam]: eventType,
      from: fromInDayJsFormat.clone().startOf('day').format(MOMENT_FORMATS.javaLocalDateFormat),
      to: toInDayJsFormat.clone().add(1, 'day').format(MOMENT_FORMATS.javaLocalDateFormat),
      zoneOffset: getTimezoneOffset(),
    }
    if (clinicBrandIds && !!clinicBrandIds?.length) {
      params[clinicBrandParam] = clinicBrandIds?.join(',')
    }
    if (clinicIds && !!clinicIds?.length) {
      params[clinicLocationParam] = clinicIds?.join(',')
    }
    const resp = await getSPDataWithParams(reportFetchingApiURL, params)
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const scheduledPatientsData = getDataMapFromCommonResponse(resp)
        setScheduledPatientsData(scheduledPatientsData)
      }
    }
    setLoadingTableData(false)
  }

  useEffect(() => {
    console.log({
      toInDayJsFormat,
      fromInDayJsFormat,
      eventType,
      isEventTypesNotMandatory,
    })

    if (!isEventTypesNotMandatory) {
      if (toInDayJsFormat && fromInDayJsFormat && eventType && !loadingTableData) {
        getScheduledPatients()
      }
    } else {
      if (toInDayJsFormat && fromInDayJsFormat && !loadingTableData) {
        getScheduledPatients()
      }
    }
  }, [clinicBrandIds, eventType, toInDayJsFormat, fromInDayJsFormat])

  return (
    <BrandPayoutStyledSmartTable>
      <CSmartTable
        items={(
          scheduledPatientsData?.[eventType] ||
          scheduledPatientsData?.['patient-appointment-info'] ||
          []
        )?.map(listingItemTransformer)}
        columns={columns({
          hideClinicDetails,
          hideContactDetails,
          patientVisitInfoLabel,
        })}
        loading={loadingTableData}
        tableProps={{
          hover: true,
          striped: true,
          responsive: true,
        }}
        // columnSorter
        // pagination
        // onColumnFilterChange={(filter) => {
        //   getDataWithSortPageOrSize(1)
        //   setColumnFilter(filter)
        // }}
        // onItemsPerPageChange={(pageSize) => {
        //   getDataWithSortPageOrSize(1, undefined, pageSize)
        //   setItemsPerPage(pageSize)
        // }}
        itemsPerPage={10000000000000000}
        scopedColumns={scopedColumns({})}
      />
    </BrandPayoutStyledSmartTable>
  )
}

function columns({
  hideClinicDetails = false,
  hideContactDetails,
  patientVisitInfoLabel = 'Visit Information',
}) {
  return [
    getCSmartTableColumnData({
      key: 'appointmentInfo',
      label: 'Appointment',
      filter: false,
      sorter: true,
      _style: { minWidth: '240px', width: '260px' },
    }),
    getCSmartTableColumnData({
      key: 'patientSummary',
      label: 'Patient & Contact',
      filter: false,
      sorter: false,
      _style: { minWidth: '260px', width: '280px' },
    }),
    getCSmartTableColumnData({
      key: 'clinicSummary',
      label: 'Clinic & Practitioner',
      filter: false,
      sorter: false,
      _style: { minWidth: '220px', width: '240px' },
    }),
  ]
}

function scopedColumns() {
  return {
    appointmentInfo: (patInfo) => {
      const latestAppt = patInfo?.latestAppointmentInfoDetails
      const start = latestAppt?.startTime ? dayjs.utc(latestAppt?.startTime).local() : null
      return (
        <td style={{ padding: '12px', verticalAlign: 'top' }}>
          {!!latestAppt ? (
            <div className="d-flex flex-column gap-1" style={{ minWidth: '180px' }}>
              <span style={{ color: '#333', fontSize: '14px', fontWeight: 500 }}>
                {start?.format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)}
              </span>
              <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
                ({getOrdinalSuffix(latestAppt?.patientMeetingSeqNumber)} Visit)
              </span>
              <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
                {getExactDiffFromCurrentTimeDayJs(start)}
              </span>
            </div>
          ) : (
            <span style={{ color: '#8f8f9f' }}>--</span>
          )}
        </td>
      )
    },
    patientSummary: (patientInfo) => {
      const phone = patientInfo?.mobile ? formatPhoneNumber(patientInfo?.mobile) : null
      return (
        <td style={{ padding: '12px', verticalAlign: 'top' }}>
          <div className="d-flex flex-column gap-1" style={{ minWidth: '200px' }}>
            <div className="d-flex align-items-center gap-1">
              <span style={{ color: '#333', fontSize: '14px', fontWeight: 500 }}>
                {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
              </span>
            </div>
            <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
              {getAgeFromDobWithTime(patientInfo?.dateOfBirth)} Y / {capitalize(patientInfo?.gender)}
            </span>
            <div className="d-flex flex-column gap-1" style={{ fontSize: '13px' }}>
              <span style={{ color: '#8f8f9f' }}>
                <strong>Patient #</strong>: {patientInfo?.patientNumber || 'Not assigned'}
              </span>
              <span style={{ color: '#8f8f9f' }}>
                <strong>UHID</strong>: {patientInfo?.uhid || '--'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-1 flex-wrap" style={{ minWidth: '180px' }}>
              {phone ? (
                <span style={{ fontSize: '14px' }}>
                  <span style={{ color: '#8f8f9f' }}>({phone.countryCode})</span>{' '}
                  <span style={{ color: '#333' }}>{phone.formattedPhoneNumber}</span>
                  <IconButton
                    size="small"
                    className="flex-shrink-0"
                    onClick={(event) => {
                      event.stopPropagation()
                      copyToClipBoard('Mobile', patientInfo?.mobile)
                    }}
                    sx={{ padding: '4px', marginLeft: '4px' }}
                  >
                    <CopyAll fontSize="small" />
                  </IconButton>
                </span>
              ) : (
                <span style={{ fontSize: '14px', color: '#333' }}>
                  {patientInfo?.email || '--'}
                  {patientInfo?.email && (
                    <IconButton
                      size="small"
                      className="flex-shrink-0"
                      onClick={(event) => {
                        event.stopPropagation()
                        copyToClipBoard('Email', patientInfo?.email)
                      }}
                      sx={{ padding: '4px', marginLeft: '4px' }}
                    >
                      <CopyAll fontSize="small" />
                    </IconButton>
                  )}
                </span>
              )}
            </div>
          </div>
        </td>
      )
    },
    clinicSummary: (patientInfo) => {
      const showPractitionerDetails = !!patientInfo?.mentorDetails
      return (
        <td style={{ padding: '12px', verticalAlign: 'top' }}>
          <div className="d-flex flex-column gap-1" style={{ minWidth: '200px' }}>
            {showPractitionerDetails && (
              <span style={{ fontSize: '14px', marginBottom: '4px' }}>
                <strong style={{ color: '#333' }}>Practitioner:</strong>{' '}
                <span style={{ color: '#333' }}>{patientInfo?.mentorDetails?.name}</span>{' '}
                <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
                  {patientInfo?.mentorDetails?.categories?.includes('doctor') ||
                  patientInfo?.mentorDetails?.categories?.includes('psychiatrist')
                    ? '(Consulting Doctor)'
                    : '(Consulting Therapist)'}
                </span>
              </span>
            )}
            {patientInfo?.brandData && (
              <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
                <strong>Clinic:</strong>{' '}
                {patientInfo?.brandData?.brandName || patientInfo?.brandData?.title}
              </span>
            )}
            {patientInfo?.clinicData && (
              <span style={{ color: '#8f8f9f', fontSize: '13px' }}>
                <strong>Location:</strong> {patientInfo?.clinicData?.title || 'Nil'}
              </span>
            )}
          </div>
        </td>
      )
    },
    'importData.importedFromPatientNumber': (patientInfo) => {
      // brand
      // locaiont
      // regon
      return (
        <td>
          {patientInfo?.mergeData ? (
            <div className="d-flex flex-column align-items-center gap-1">
              {patientInfo?.mergeData?.merged ? (
                <Chip label="Merging Completed" color="success" />
              ) : (
                <Chip label="Merging Pending" color="warning" />
              )}
            </div>
          ) : (
            <Chip label="Merging Not Required" />
          )}
          {patientInfo?.importData && (
            <div className="d-flex flex-column gap-1">
              <span>
                <strong>Import Id</strong>: {patientInfo?.importData?.importedFrom}-
                {patientInfo?.importData?.importedFromId}{' '}
                <IconButton
                  className="flex-shrink-0"
                  onClick={(event) => {
                    event.stopPropagation()
                    copyToClipBoard(
                      'Import ID',
                      `${patientInfo?.importData?.importedFrom}-
                  ${patientInfo?.importData?.importedFromId}`,
                    )
                  }}
                >
                  <CopyAll />
                </IconButton>
              </span>
              <span>
                <strong>Imported On</strong>:{' '}
                {dayjs
                  .utc(patientInfo?.importData?.importedOn)
                  .local()
                  .format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)}
              </span>
              <span></span>
            </div>
          )}
        </td>
      )
    },
    visitInfo: (patientInfo) => {
      const dateStringObject = getExactDiffFromCurrentTimeDayJsWithExtraData(
        dayjs.utc(patientInfo?.proposedVisitDate).local(),
      )

      return (
        <td style={{ padding: '12px', verticalAlign: 'top' }}>
          <div className="d-flex gap-2 align-items-start" style={{ minWidth: '200px' }}>
            {!!patientInfo?.upcomingAppointmentInfoDetails ? (
              <ListingVerticalStripForEventStatus
                containerStyles={{ padding: '8px', flexShrink: 0 }}
                bookedEventData={patientInfo?.upcomingAppointmentInfoDetails}
              />
            ) : (
              <VerticalStripForEventStatusUIOnly
                containerStyles={{ padding: '8px', flexShrink: 0 }}
                backgroundColor="#353535"
                statusText="Yet to be booked"
              />
            )}
            <div className="d-flex flex-column gap-1" style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ color: '#000', fontSize: '14px' }}>
                {dayjs.utc(patientInfo?.proposedVisitDate).local().format('DD-MMM-YYYY')}
              </strong>
              <span style={{ color: dateStringObject.isPastDate ? 'red' : '#666', fontSize: '13px' }}>
                {getExactDiffFromCurrentTimeDayJs(dayjs(patientInfo?.proposedVisitDate))}
              </span>
            </div>
          </div>
        </td>
      )
    },
  }
}
